// Fullscreen mount of the complete copied V2 shell (ui-v2.js) inside
// `.race-viewer` — the actual fullscreen element — so native and fallback
// fullscreen receive identical controls. This component performs no
// rendering of its own betting UI: it activates the copied shell, then
// drives setPhase/setTransaction/setLive/setResult from HRW state (race
// clock, market window, the shared placement machine, deterministic
// playback physics, and settled coupons).

import { X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Link } from "wouter";

import type { Racecard } from "@/api";
import { useCapabilities } from "@/capabilities/CapabilityProvider";
import { useWalletBalance } from "@/cashier/useWalletBalance";
import { useChat } from "@/chat/ChatProvider";
import { ChatSurface } from "@/chat/ChatSurface";
import {
  OverlaySelect,
  type OverlaySelectOption,
} from "@/components/OverlaySelect";
import { money } from "@/components/ui";
import { stageTitle, trackTitle, weatherTitle } from "@/features/racing/venue";
import { overlayCountdownText, type useRaceClock } from "@/hooks/useRaceClock";
import type { CameraMode, RacePlayback } from "@/renderer/RaceRenderer";
import { useSession } from "@/session/SessionProvider";

import type { ToteAdapter, ToteLine } from "./hrwTote";
import {
  emptyShellTicketMemory,
  planShellTicketPush,
} from "./shellTicketMirror";
import { uiV2StylesheetHref } from "./stylesheet";
import {
  MIN_BET_CENTS,
  type MarketData,
  type PlacementState,
  toteFieldFromCard,
} from "./useV2Betting";

type V2Shell = import("@renderer/ui-v2.js").V2Shell;

const ENVIRONMENT_IDS: Record<string, string> = {
  "canyon:clear": "canyon-day",
  "canyon:rain": "canyon-storm",
  "rose:mist": "rose-dusk",
  "desert:clear": "dune-gold",
  "desert:dust": "dune-storm",
  "midnight:clear": "midnight",
};

export const CAMERA_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "cinematic", label: "Cinematic" },
  { value: "rail", label: "Rail" },
  { value: "crane", label: "Crane" },
  { value: "ride", label: "Ride" },
] satisfies readonly OverlaySelectOption<CameraMode>[];

function environmentId(card: Racecard): string {
  return ENVIRONMENT_IDS[`${card.stage}:${card.weather}`] ?? "canyon-day";
}

function environmentLabel(card: Racecard): string {
  return `${stageTitle(card)} ${weatherTitle(card)}`.trim();
}

export type RaceCouponsModel = {
  acceptedLines: ToteLine[];
  settledLines: Array<ToteLine & { returnCents: number | null }>;
  stakeCents: number;
  returnedCents: number;
  hasTicket: boolean;
  settlementPending: boolean;
  allSettled: boolean;
  /** Durable ticket identity — earliest coupon id and server placedAt. */
  reference: string | null;
  placedAt: string | null;
  couponIds: string[];
};

export function RaceTheater({
  active,
  bettingCountdownLabel,
  viewer,
  card,
  racecardHash,
  market,
  playback,
  clock,
  tote,
  placement,
  coupons,
  canRebet,
  getUnitStake,
  setUnitStake,
  onPlace,
  onCheckStatus,
  onRebet,
  onContinue,
  nextRaceStartsAt = null,
  resultCountdown = "",
  resultReady = true,
  finishReplayActive = false,
  soundEnabled,
  onToggleSound,
  cameraMode,
  setCameraMode,
  rideGate,
  setRideGate,
  onResultPrint,
  onOfficialResult,
}: {
  active: boolean;
  bettingCountdownLabel: string;
  viewer: HTMLElement | null;
  card: Racecard;
  racecardHash: string | null;
  market: MarketData | null | undefined;
  playback: RacePlayback | null;
  clock: ReturnType<typeof useRaceClock>;
  tote: ToteAdapter;
  placement: PlacementState;
  coupons: RaceCouponsModel;
  canRebet: boolean;
  getUnitStake: () => number;
  setUnitStake: (cents: number) => void;
  onPlace: () => void;
  onCheckStatus: () => void;
  onRebet: () => void;
  onContinue: () => void;
  /** The next race's published scheduled_at (public races-list data) for
   * the intermission capsule countdown; null across schedule gaps. */
  nextRaceStartsAt?: string | null;
  /** Continuous-broadcast copy ("Continuing in Ns") for the result footer. */
  resultCountdown?: string;
  /** The official terminal waits for the finish replay to hand over. */
  resultReady?: boolean;
  /** The slow-motion podium replay is on screen right now. */
  finishReplayActive?: boolean;
  soundEnabled: boolean;
  onToggleSound: () => void;
  /** View controls the HUD owns in fullscreen (the page toolbar hides). */
  cameraMode: CameraMode;
  setCameraMode: (mode: CameraMode) => void;
  rideGate: number;
  setRideGate: (gate: number) => void;
  onResultPrint?: (event: string, payload?: Record<string, unknown>) => void;
  onOfficialResult?: (netCents: number, printed: boolean) => void;
}) {
  const { session, loading: sessionLoading } = useSession();
  const authenticated = Boolean(session);
  const capabilities = useCapabilities();
  const chat = useChat();
  const balance = useWalletBalance(session?.playerId ?? null);

  const appHostRef = useRef<HTMLDivElement>(null);
  const chatDockRef = useRef<HTMLDivElement>(null);
  const fairnessRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<V2Shell | null>(null);
  const shellAuthRef = useRef<boolean | null>(null);
  const activatingRef = useRef(false);
  const [shell, setShell] = useState<V2Shell | null>(null);
  const [activationRevision, setActivationRevision] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const setFairnessLayer = useCallback((layer: HTMLDivElement | null) => {
    fairnessRef.current = layer;
    if (layer && layer.dataset.overlayInitialized !== "true") {
      layer.hidden = true;
      layer.dataset.overlayInitialized = "true";
    }
  }, []);
  // Per-shell transaction bookkeeping (reset on rollback/re-activation).
  const mirrorMemoryRef = useRef(emptyShellTicketMemory());

  // Command handlers read live React state through refs so the one-time
  // shell activation never captures stale closures. Refs update in an
  // effect — never during render.
  const actionsRef = useRef({
    onPlace,
    onCheckStatus,
    onRebet,
    onContinue,
    onToggleSound,
    onResultPrint,
  });
  const sessionRef = useRef(session);
  const currencyRef = useRef(session?.currency ?? "USD");
  const clockOffsetRef = useRef(clock.serverOffsetMs);
  useEffect(() => {
    actionsRef.current = {
      onPlace,
      onCheckStatus,
      onRebet,
      onContinue,
      onToggleSound,
      onResultPrint,
    };
    sessionRef.current = session;
    currencyRef.current = session?.currency ?? "USD";
    clockOffsetRef.current = clock.serverOffsetMs;
  });

  // ------------------------------------------------------------ activation
  useEffect(() => {
    if (!active || sessionLoading || shellRef.current || activatingRef.current)
      return;
    activatingRef.current = true;
    let cancelled = false;
    void (async () => {
      const { activateHorseRacingV2 } = await import("@renderer/ui-v2.js");
      if (cancelled || !appHostRef.current) return;
      const canvas =
        viewer?.querySelector<HTMLElement>(".race-renderer-stack canvas") ??
        appHostRef.current;
      const activated = await activateHorseRacingV2({
        tote,
        fmtUSD: (cents: number) => money(cents, currencyRef.current),
        config: { minBet: MIN_BET_CENTS, maxBet: null },
        mode: "sp",
        getUnitStake,
        setUnitStake,
        initialState: {
          balance: 0,
          environment: environmentId(card),
          environmentChrome: environmentId(card),
          environmentLabel: environmentLabel(card),
          soundEnabled,
          distanceMeters: card.distanceM,
          lapDistanceMeters: card.lapDistanceM,
        },
        commands: authenticated
          ? {
              place: () => {
                if (!sessionRef.current) return;
                actionsRef.current.onPlace();
              },
              checkStatus: () => actionsRef.current.onCheckStatus(),
              rebet: () => actionsRef.current.onRebet(),
              toggleSound: () => actionsRef.current.onToggleSound(),
              openChat: () => setChatOpen((open) => !open),
              openFairness: () => {
                const layer =
                  fairnessRef.current ??
                  document
                    .querySelector(".v2-theater-fairness")
                    ?.closest<HTMLDivElement>(".hr-v2__dialog-layer") ??
                  null;
                if (!layer) return null;
                fairnessRef.current = layer;
                layer.hidden = false;
                return layer;
              },
              continueRace: () => actionsRef.current.onContinue(),
              onResultPrint: (
                event: string,
                payload?: Record<string, unknown>,
              ) => actionsRef.current.onResultPrint?.(event, payload),
            }
          : {
              continueRace: () => actionsRef.current.onContinue(),
              onResultPrint: (
                event: string,
                payload?: Record<string, unknown>,
              ) => actionsRef.current.onResultPrint?.(event, payload),
            },
        hosts: {
          app: appHostRef.current,
          canvas,
          chatDock: authenticated ? chatDockRef.current : null,
        },
        chatAvailable: authenticated,
        chatLabel: "Community chat",
        spectatorOnly: !authenticated,
        stylesheetHref: uiV2StylesheetHref,
      });
      if (cancelled) {
        activated.rollback();
        return;
      }
      shellRef.current = activated;
      shellAuthRef.current = authenticated;
      // A fresh shell shows nothing yet; the mirror re-derives from scratch.
      mirrorMemoryRef.current = emptyShellTicketMemory();
      setShell(activated);
    })()
      .catch((error: unknown) => {
        console.error("Horse Racing V2 fullscreen activation failed.", error);
      })
      .finally(() => {
        activatingRef.current = false;
      });
    return () => {
      cancelled = true;
      activatingRef.current = false;
    };
  }, [
    activationRevision,
    active,
    authenticated,
    card,
    getUnitStake,
    sessionLoading,
    setUnitStake,
    soundEnabled,
    tote,
    viewer,
  ]);

  useEffect(() => {
    const current = shellRef.current;
    if (!active || !current || shellAuthRef.current === authenticated) return;
    current.rollback();
    shellRef.current = null;
    shellAuthRef.current = null;
    setShell(null);
    setChatOpen(false);
    mirrorMemoryRef.current = emptyShellTicketMemory();
    setActivationRevision((revision) => revision + 1);
  }, [active, authenticated]);

  // Leaving fullscreen (or unmounting) rolls the overlay back completely —
  // the donor shell restores every suppressed element and the chat dock.
  // State clears via render-phase adjustment; the imperative rollback runs
  // in the effect.
  if (!active && shell) setShell(null);
  if (!active && chatOpen) setChatOpen(false);
  useEffect(() => {
    if (active) return;
    shellRef.current?.rollback();
    shellRef.current = null;
    shellAuthRef.current = null;
    // A future re-activation rehydrates from scratch.
    mirrorMemoryRef.current = emptyShellTicketMemory();
  }, [active]);
  useEffect(
    () => () => {
      shellRef.current?.rollback();
      shellRef.current = null;
      shellAuthRef.current = null;
    },
    [],
  );

  // -------------------------------------------------------------- context
  useEffect(() => {
    if (!shell) return;
    shell.update({
      field: toteFieldFromCard(card),
      distanceMeters: card.distanceM,
      lapDistanceMeters: card.lapDistanceM,
      environment: environmentId(card),
      environmentChrome: environmentId(card),
      environmentLabel: environmentLabel(card),
      round: `${trackTitle(card)} · ${card.distanceM}m`,
    });
  }, [card, shell]);

  useEffect(() => {
    if (!shell) return;
    shell.update({ balance: balance.data?.balanceCents ?? 0 });
  }, [balance.data?.balanceCents, shell]);

  useEffect(() => {
    if (!shell) return;
    shell.update({ soundEnabled, canRebet });
  }, [canRebet, shell, soundEnabled]);

  // Continuous broadcast: the result footer counts down to the next round
  // (the page-level scheduler owns the actual advance).
  useEffect(() => {
    if (!shell) return;
    shell.setResultCountdown(resultCountdown);
  }, [resultCountdown, shell]);

  // Community chat: same provider state as the onsite drawer — the shell
  // only mirrors the unread badge and toggles the dock's visibility.
  useEffect(() => {
    if (!shell) return;
    shell.update({ chatUnread: chatOpen ? 0 : chat.snapshot.unreadCount });
  }, [chat.snapshot.unreadCount, chatOpen, shell]);

  useEffect(() => {
    if (!chatOpen) return;
    chat.markViewed();
  }, [chat, chatOpen, chat.snapshot.unreadCount]);

  // ---------------------------------------------------------- transaction
  // One planner derives what the shell's ticket surface must show from the
  // placement machine (in-flight), the coupons read model (the durable
  // accepted ticket) and the tote draft (the player's next ticket). The
  // planner is pure and signature-guarded: the shell only receives real
  // transitions, and an accepted ticket can never be wiped by the placement
  // machine's receipt reset or its hand-over to the next betting window.
  const toteSnapshot = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) =>
        tote.subscribe(() => onStoreChange(), { emitCurrent: false }),
      [tote],
    ),
    tote.getSnapshot,
    tote.getSnapshot,
  );
  useEffect(() => {
    if (!shell) return;
    // The result terminal owns the shell during the official phase; the
    // planner only runs there to clear state for a NEW viewed race (the
    // donor's advance-to-next-card path).
    if (
      clock.phase === "official" &&
      mirrorMemoryRef.current.raceId === card.raceId
    ) {
      return;
    }
    const { command, memory } = planShellTicketPush(mirrorMemoryRef.current, {
      raceId: card.raceId,
      placement,
      coupons: {
        hasTicket: coupons.hasTicket,
        acceptedLines: coupons.acceptedLines,
        stakeCents: coupons.stakeCents,
        reference: coupons.reference,
        placedAt: coupons.placedAt,
        couponIds: coupons.couponIds,
      },
      bettingOpen: market?.bettingOpen ?? false,
      draftCount: toteSnapshot.lines.length,
      formatAmount: (cents: number) => money(cents, currencyRef.current),
    });
    mirrorMemoryRef.current = memory;
    if (command) shell.setTransaction(command.transaction, command.detail);
  }, [
    card.raceId,
    clock.phase,
    coupons,
    market?.bettingOpen,
    placement,
    shell,
    toteSnapshot,
  ]);

  // --------------------------------------------------------------- phases
  const raceTickerActive = clock.phase === "running" && Boolean(playback);
  // Donor cadence: "Bets close in N seconds" announces at 10/5 only on the
  // transition into that second (the effect re-runs on every clock tick).
  const lastCloseSecondsRef = useRef<number | null>(null);
  useEffect(() => {
    if (!shell || raceTickerActive || clock.phase === "official") return;
    const capabilityBlocked = Boolean(
      session && !capabilities.loading && !capabilities.can("platform_betting"),
    );
    // The capsule countdown (PLAN §"Racing V2 interface" → "Fullscreen
    // countdown"): same clock values as the page titlebar, formatted once.
    const capsuleCountdown = overlayCountdownText({
      phase: clock.phase,
      bettingOpen: Boolean(market?.bettingOpen),
      countdownMs: clock.countdownMs,
      bettingCountdownMs: clock.bettingCountdownMs,
    });
    if (clock.phase === "betting" && market?.bettingOpen) {
      const closeSeconds = Math.ceil(
        Math.max(0, clock.bettingCountdownMs) / 1000,
      );
      const announceClose = closeSeconds !== lastCloseSecondsRef.current;
      lastCloseSecondsRef.current = closeSeconds;
      shell.setPhase("betting", {
        ...(capsuleCountdown ? { text: capsuleCountdown } : {}),
        ...(announceClose ? { closeSeconds } : {}),
        close: capabilityBlocked
          ? "Betting is not available in your region"
          : session
            ? `Closes in ${bettingCountdownLabel}`
            : "Sign in to bet",
        round: `${trackTitle(card)} · ${card.distanceM}m`,
      });
      return;
    }
    lastCloseSecondsRef.current = null;
    if (clock.phase === "scheduled") {
      shell.setPhase("locked", {
        text: capsuleCountdown ?? "BETTING OPENS SOON",
        close: "Betting opens soon",
      });
      return;
    }
    // Market locked, gates not open yet: 5-4-3-2-1 countdown capsule.
    const seconds = Math.ceil(clock.countdownMs / 1000);
    if (seconds > 0 && seconds <= 5) {
      shell.setPhase("countdown", {
        count: seconds,
        ...(capsuleCountdown ? { text: capsuleCountdown } : {}),
      });
    } else if (clock.phase === "starting") {
      shell.setPhase("locked", { text: "PREPARING THE TRACK" });
    } else {
      shell.setPhase("locked");
    }
  }, [
    capabilities,
    card,
    clock.bettingCountdownMs,
    clock.countdownMs,
    clock.phase,
    market?.bettingOpen,
    raceTickerActive,
    session,
    shell,
    bettingCountdownLabel,
  ]);

  // ------------------------------------------------------------ live race
  const playbackKey = playback
    ? `${playback.raceId}:${playback.startedAt}`
    : null;
  useEffect(() => {
    if (!shell || !playback || !playbackKey || clock.phase !== "running") {
      return;
    }
    if (playback.raceId !== card.raceId) return;
    let stopped = false;
    let timer: number | null = null;
    let frozen = false;
    let started = false;
    const gatesOpenAt = Date.parse(playback.gatesOpenAt);

    void import("@renderer/race-physics.js").then(
      ({ planRace, raceSituation }) => {
        if (stopped) return;
        const plan = planRace(playback.physics);
        const lateJoin =
          (Date.now() + clockOffsetRef.current - gatesOpenAt) / 1_000 > 2;

        const tick = () => {
          if (stopped) return;
          const t = (Date.now() + clockOffsetRef.current - gatesOpenAt) / 1_000;
          if (t >= 0) {
            const situation = raceSituation(plan, t);
            const standing = [...situation.samples.keys()].sort(
              (a, b) => situation.samples[b].s - situation.samples[a].s,
            );
            if (!started) {
              started = true;
              if (!lateJoin) shell.setPhase("start");
            }
            if (t >= plan.firstFinish) {
              if (!frozen) {
                frozen = true;
                shell.finishLive({ standing, photo: situation.photoFinish });
              }
              return; // the result driver takes over at official
            }
            shell.setLive({
              raceId: card.raceId,
              standing,
              finalDrive: plan.firstFinish - t < 3.6,
              lateJoin: lateJoin && started,
              distanceMeters: card.distanceM,
            });
          }
          timer = window.setTimeout(tick, 250);
        };
        tick();
      },
    );

    return () => {
      stopped = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [card.distanceM, card.raceId, clock.phase, playback, playbackKey, shell]);

  // During the slow-motion podium replay the capsule reads the donor's
  // FINISH REPLAY call — the terminal waits for the hand-off.
  useEffect(() => {
    if (!shell || !finishReplayActive) return;
    shell.setPhase("photo", { text: "FINISH REPLAY" });
  }, [finishReplayActive, shell]);

  // -------------------------------------------------------------- result
  const printedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!shell || !playback || playback.status !== "official") return;
    if (playback.raceId !== card.raceId) return;
    if (!resultReady) return;
    const pending = coupons.hasTicket && !coupons.allSettled;
    const finishTimes = playback.finishTimesMs;
    const order = playback.order;
    const photoFinish =
      order.length > 1 &&
      Math.abs(finishTimes[order[1]] - finishTimes[order[0]]) < 120;
    const net = coupons.returnedCents - coupons.stakeCents;
    const printKey = `hrw:${card.raceId}`;
    const animate = !pending && printedRef.current !== printKey;
    if (animate) printedRef.current = printKey;
    void shell
      .setResult(
        {
          official: true,
          settlementPending: pending,
          field: toteFieldFromCard(card),
          order,
          finishTimesMs: finishTimes,
          marginsL100: playback.marginsL100 ?? [],
          photoFinish,
          stake: coupons.stakeCents,
          totalReturn: coupons.returnedCents,
          net,
          hasTicket: coupons.hasTicket,
          lines: coupons.settledLines,
          distanceMeters: card.distanceM,
          lapDistanceMeters: card.lapDistanceM,
          nextCopy: "Next race ready",
          raceId: card.raceId,
        },
        { animateResult: animate, printKey },
      )
      .then((printed) => {
        if (!pending) onOfficialResult?.(net, printed);
      });
  }, [card, coupons, onOfficialResult, playback, resultReady, shell]);

  // ---------------------------------------------------------- intermission
  // Once the official board is up and the loop is advancing (the result
  // hold, or waiting on the schedule for the next card), the capsule
  // answers "when does the next race start" (PLAN §"Racing V2 interface"
  // → "Fullscreen countdown"). The shell's intermission phase keeps the
  // result board on screen while the capsule carries the countdown; the
  // milliseconds derive from the same clock as every other capsule
  // number. Archive replays and pending settlements never produce a
  // resultCountdown, so they keep the plain OFFICIAL presentation.
  const intermissionCapsule =
    clock.phase === "official" && resultCountdown
      ? overlayCountdownText({
          phase: clock.phase,
          bettingOpen: false,
          countdownMs: clock.countdownMs,
          bettingCountdownMs: clock.bettingCountdownMs,
          intermission: true,
          nextRaceStartMs:
            nextRaceStartsAt === null
              ? null
              : Date.parse(nextRaceStartsAt) - clock.nowMs,
        })
      : null;
  useEffect(() => {
    if (!shell || !intermissionCapsule) return;
    shell.setPhase("intermission", { text: intermissionCapsule });
  }, [intermissionCapsule, shell]);

  // ---------------------------------------------------------------- render
  return (
    <div className="v2-theater-host">
      <div ref={appHostRef} />
      {active ? (
        <div
          aria-label="3D camera controls"
          className="v2-theater-cameras"
          data-camera-mode={cameraMode}
          role="group"
        >
          <OverlaySelect
            ariaLabel="Camera mode"
            className="v2-theater-cameras__camera-select"
            onChange={setCameraMode}
            options={CAMERA_OPTIONS}
            value={cameraMode}
          />
          {(["auto", "cinematic", "rail", "crane", "ride"] as CameraMode[]).map(
            (camera) => (
              <button
                aria-pressed={cameraMode === camera}
                className="v2-theater-cameras__camera"
                key={camera}
                onClick={() => setCameraMode(camera)}
                type="button"
              >
                {camera}
              </button>
            ),
          )}
          {cameraMode === "ride" ? (
            <OverlaySelect
              ariaLabel="Ride camera horse"
              className="v2-theater-cameras__ride-select"
              onChange={setRideGate}
              options={card.runners.map((runner) => ({
                value: runner.gate,
                label: `#${runner.gate + 1} ${runner.name}`,
              }))}
              value={rideGate}
            />
          ) : null}
        </div>
      ) : null}
      {authenticated ? (
        <div hidden={!chatOpen} ref={chatDockRef}>
          {chatOpen ? (
            <div
              aria-label="Community chat"
              className="v2-theater-chat"
              role="dialog"
            >
              <div className="v2-theater-chat__header">
                <strong>Community chat</strong>
                <button
                  aria-label="Close chat"
                  onClick={() => setChatOpen(false)}
                  type="button"
                >
                  <X aria-hidden size={16} />
                </button>
              </div>
              <div className="v2-theater-chat__body">
                <ChatSurface />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {authenticated ? (
        <div className="hr-v2__dialog-layer" ref={setFairnessLayer}>
          <section
            aria-label="Race fairness"
            className="v2-theater-fairness"
            role="dialog"
          >
            <h2>Provably fair race</h2>
            <p>
              This race was committed before betting opened. Field, weights and
              outcome derive from the published racecard and pre-committed seed
              — recompute everything in your browser.
            </p>
            <dl>
              <dt>Race</dt>
              <dd>{card.raceId}</dd>
              <dt>Racecard hash</dt>
              <dd>{racecardHash ?? "—"}</dd>
              <dt>Exact RTP</dt>
              <dd>{(card.rtpBp / 100).toFixed(2)}%</dd>
            </dl>
            <div className="v2-theater-fairness__actions">
              <Link href={`/app/verify/${card.raceId}`}>
                Open full verifier
              </Link>
              <button
                onClick={(event) => {
                  const layer = event.currentTarget.closest<HTMLDivElement>(
                    ".hr-v2__dialog-layer",
                  );
                  if (layer) layer.hidden = true;
                }}
                type="button"
              >
                Close
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
